import { getLiveChatAgentContext, getLiveChatWebSocketURL } from 'src/utils/liveChatNotifications';
import { CommonHelper } from 'src/utils/commonHelper';

const listeners = new Set();
const openListeners = new Set();
const errorListeners = new Set();

let socket = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let shouldReconnect = false;
let currentContextKey = '';

const getContextKey = (context) => `${context.userId || 0}:${context.brandId || 0}:${context.orgId || 0}`;

const isDemoAccount = () => CommonHelper.GetBrandInfoFromLocalStorage('is_demo_account') === true;

const clearReconnectTimer = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const emitMessage = (data) => {
  listeners.forEach((listener) => {
    try {
      listener(data);
    } catch (_error) {
      // Keep one subscriber from breaking the shared live chat stream.
    }
  });
};

const emitOpen = () => {
  openListeners.forEach((listener) => {
    try {
      listener();
    } catch (_error) {}
  });
};

const emitError = (error) => {
  errorListeners.forEach((listener) => {
    try {
      listener(error);
    } catch (_error) {}
  });
};

export const connectLiveChatSocket = () => {
  if (isDemoAccount()) {
    shouldReconnect = false;
    clearReconnectTimer();
    if (socket) {
      socket.onclose = null;
      socket.close();
      socket = null;
    }
    currentContextKey = 'demo';
    return null;
  }

  const agentContext = getLiveChatAgentContext();
  if (!agentContext.userId || !agentContext.brandId) {
    return null;
  }

  shouldReconnect = true;
  const nextContextKey = getContextKey(agentContext);
  if (
    socket &&
    currentContextKey === nextContextKey &&
    (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
  ) {
    return socket;
  }

  clearReconnectTimer();
  if (socket) {
    socket.onclose = null;
    socket.close();
    socket = null;
  }

  currentContextKey = nextContextKey;
  socket = new WebSocket(getLiveChatWebSocketURL());

  socket.onopen = () => {
    reconnectAttempts = 0;
    socket.send(
      JSON.stringify({
        type: 'agent_register',
        payload: {
          user_id: agentContext.userId,
          brand_id: agentContext.brandId,
          org_id: agentContext.orgId,
        },
      })
    );
    emitOpen();
  };

  socket.onmessage = (event) => {
    try {
      emitMessage(JSON.parse(event.data));
    } catch (_error) {}
  };

  socket.onerror = (error) => {
    emitError(error);
  };

  socket.onclose = () => {
    socket = null;
    if (!shouldReconnect) {
      return;
    }
    const delay = Math.min(1000 * 2 ** reconnectAttempts, 10000);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(connectLiveChatSocket, delay);
  };

  return socket;
};

export const addLiveChatSocketListener = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const addLiveChatSocketOpenListener = (listener) => {
  openListeners.add(listener);
  return () => openListeners.delete(listener);
};

export const addLiveChatSocketErrorListener = (listener) => {
  errorListeners.add(listener);
  return () => errorListeners.delete(listener);
};

export const sendLiveChatSocketMessage = (message) => {
  if (isDemoAccount()) {
    return true;
  }
  connectLiveChatSocket();
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return false;
  }
  socket.send(typeof message === 'string' ? message : JSON.stringify(message));
  return true;
};

export const isLiveChatSocketOpen = () => isDemoAccount() || socket?.readyState === WebSocket.OPEN;

export const stopLiveChatSocket = () => {
  shouldReconnect = false;
  clearReconnectTimer();
  if (socket) {
    socket.onclose = null;
    socket.close();
    socket = null;
  }
};
